import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Badge, Button, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faEdit, 
  faTrash, 
  faBuilding,
  faMapMarkerAlt,
  faPhone,
  faEnvelope,
  faGlobe,
  faCalendarAlt,
  faUsers,
  faSignOutAlt
} from '@fortawesome/free-solid-svg-icons';
import { companyService } from '../../services/companyService';

const CompanyDetails = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        setLoading(true);
        const companyData = await companyService.getCompanyById(companyId);
        setCompany(companyData);
      } catch (error) {
        console.error('Error fetching company details:', error);
        setError('Failed to load company details');
      } finally {
        setLoading(false);
      }
    };

    if (companyId) {
      fetchCompanyDetails();
    }
  }, [companyId]);

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete company "${company?.name}"? This action cannot be undone.`)) {
      // Handle delete logic
      navigate('/admin/companies');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <Alert variant="danger">
          <FontAwesomeIcon icon={faBuilding} className="me-2" />
          {error}
        </Alert>
        <Button variant="outline-secondary" onClick={() => navigate('/admin/companies')}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Companies
        </Button>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="container-fluid">
        <Alert variant="warning">
          Company not found
        </Alert>
        <Button variant="outline-secondary" onClick={() => navigate('/admin/companies')}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Companies
        </Button>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <Button 
            variant="outline-secondary" 
            onClick={() => navigate('/admin/companies')}
            className="me-3"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Companies
          </Button>
          <div>
            <h2 className="mb-1">{company.name}</h2>
            <p className="text-muted mb-0">
              <FontAwesomeIcon icon={faBuilding} className="me-1" />
              Property Management Company
            </p>
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button 
            variant="primary"
            onClick={() => navigate(`/admin/companies/${companyId}/edit`)}
          >
            <FontAwesomeIcon icon={faEdit} className="me-2" />
            Edit Company
          </Button>
          <Button variant="outline-danger" onClick={handleDelete}>
            <FontAwesomeIcon icon={faTrash} className="me-2" />
            Delete Company
          </Button>
        </div>
      </div>

      <Row>
        <Col lg={8}>
          {/* Company Information */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">Company Information</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">Company Name</label>
                    <p className="mb-0 fw-bold">{company.name}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">Status</label>
                    <div>
                      <Badge bg={company.status === 'active' ? 'success' : 'secondary'}>
                        {company.status}
                      </Badge>
                    </div>
                  </div>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                      Address
                    </label>
                    <p className="mb-0">{company.address || 'Not specified'}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">
                      <FontAwesomeIcon icon={faPhone} className="me-1" />
                      Phone
                    </label>
                    <p className="mb-0">{company.phone || 'Not specified'}</p>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">
                      <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                      Email
                    </label>
                    <p className="mb-0">{company.email || 'Not specified'}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">
                      <FontAwesomeIcon icon={faGlobe} className="me-1" />
                      Website
                    </label>
                    <p className="mb-0">{company.website || 'Not specified'}</p>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                      Created Date
                    </label>
                    <p className="mb-0">
                      {company.createdAt ? 
                        new Date(company.createdAt.seconds * 1000).toLocaleDateString() : 
                        'Not specified'
                      }
                    </p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">
                      <FontAwesomeIcon icon={faUsers} className="me-1" />
                      Number of Users
                    </label>
                    <p className="mb-0">{company.userCount || 0}</p>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          {/* Quick Actions */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
              <h6 className="mb-0">Quick Actions</h6>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                <Button 
                  variant="primary"
                  onClick={() => navigate(`/admin/companies/${companyId}/edit`)}
                >
                  <FontAwesomeIcon icon={faEdit} className="me-2" />
                  Edit Company
                </Button>
                <Button 
                  variant="outline-primary"
                  onClick={() => navigate('/admin/users/create', { state: { companyId } })}
                >
                  <FontAwesomeIcon icon={faUsers} className="me-2" />
                  Add User
                </Button>
                <Button 
                  variant="outline-danger"
                  onClick={handleDelete}
                >
                  <FontAwesomeIcon icon={faTrash} className="me-2" />
                  Delete Company
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CompanyDetails; 